
import { cn } from '@/utils';
import { AlertCircleIcon, AlertOctagonIcon, AlertTriangleIcon, SettingsIcon, XIcon } from 'lucide-react';
import { Toast, toast as toaster } from 'react-hot-toast';

type iToastTypes = 'info' | 'error' | 'warning' | 'success' | 'setting' | 'in_progress';

type ToastCallOptions = Partial<
  Pick<Toast, 'id' | 'icon' | 'duration' | 'ariaProps' | 'className' | 'style' | 'position' | 'iconTheme'> & {
    type?: iToastTypes;
  }
>;

const toast = (message: string, options: ToastCallOptions | undefined, content = '') => {
  const alertStyle: Record<iToastTypes, {iconColor: string, icon: React.ReactNode}> = {
    info: {
      iconColor: 'var(--primary)',
      icon: <AlertCircleIcon color="#fff" size={15} />
    },
    in_progress: {
      iconColor: 'var(--primary)',
      icon: <AlertCircleIcon color="#fff" size={15} />
    },
    error: {
      iconColor: '#c21808',
      icon: <AlertOctagonIcon color="#fff" size={15} />
    },
    warning: {
      iconColor: '#f9720a',
      icon: <AlertTriangleIcon color="#fff" size={15} />
    },
    success: {
      iconColor: '#067603',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          fill="#fff"
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="bi bi-check-circle"
          viewBox="0 0 16 16"
        >
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
          <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
        </svg>
      )
    },
    setting: {
      iconColor: '#ae1af7',
      icon: <SettingsIcon color="#fff" size={15} />
    }
  };

  return toaster(
    (msg) => {
      const alertType = options?.type || msg.type;
      const colorTheme = alertStyle[alertType as iToastTypes].iconColor;
      
      return (
        <div className={cn('relative m-0 flex max-w-[340px] items-center rounded-[8px] bg-transparent p-0 border overflow-hidden', options?.className)}
          style={{borderColor: alertStyle[options?.type as iToastTypes]?.iconColor}}
        >
          <div
            style={{
              backgroundColor: `${colorTheme}`
            }}
            className="flex h-full items-center rounded-l-[6px] px-[12px]"
          >
            {alertStyle[alertType as iToastTypes].icon}
          </div>
          <div className="py-4 bg-white w-max ps-2">
            <div className="text-sm font-semibold">{message}</div>
            {content && (
              <p className="text-xs">{content}</p>
            )}
          </div>
          {content && <div></div>}
          <div
            className={cn('rounded-end bg-white cross-icon h-full flex items-center', {
              'absolute': content,
              'p-12px': !content
            })}
          >
            <div
              className='bg-white'
              onClick={() => toaster.dismiss(msg.id)}>
              <XIcon size={18} className='cursor-pointer me-2 ms-4'/>
            </div>
          </div>
        </div>
      );
    },
    {
      duration: 10000,
      ...options,
      icon: null,
      className: '!bg-transparent z-50 !p-0 !m-0',
      style: {boxShadow:'none'}
    }
  );
};

export default toast;
